import { useState, useEffect, useCallback, useRef } from 'react';
import styles from './AddProduct.module.css';
import myAxios from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { EditorState, ContentState, convertFromHTML } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import MainImageSection from './MainImageSection';
import { useNavigate, useParams } from 'react-router-dom';
import VariantModal from './VariantModal';
import AIProductModal from './AIProductModal';

// Section imports
import BasicInformation from './sections/BasicInformation';
import PricingInventory from './sections/PricingInventory';
import CategorySection from './sections/CategorySection';
import VariantsSection from './sections/VariantsSection';
import ProductOptions from './sections/ProductOptions';
import SEOSettings from './sections/SEOSettings';
import SEOPreview from './sections/SEOPreview';
import ProductFAQ from './sections/ProductFAQ';
import ProductReviews from './sections/ProductReviews';
import BreadcrumbSettings from './sections/BreadcrumbSettings';
import SchemaMarkup from './sections/SchemaMarkup';
import SEOAudit from './sections/SEOAudit';

import {
    handleFieldChange,
    uploadCallback,
    handleImageSetWrapperHelper,
    ImageUpload,
    PRODUCT_CODE_MAX,
    productCodeTooLong,
} from './HelperFunction';

function AddProduct() {
    const { id } = useParams(); // Get product ID from URL for edit mode
    const isEditMode = Boolean(id); // Determine if we're in edit or add mode

    // Product-limit heads-up (add mode only): fetch the seller's plan usage so
    // we can warn before the backend hard-blocks at 100%.
    const [limitInfo, setLimitInfo] = useState(null);
    useEffect(() => {
        if (isEditMode) return;
        const u = localStorage.getItem('id');
        if (!u) return;
        myAxios
            .get(`/get-subscription/${u}`)
            .then((res) => {
                const d = res.data?.data || res.data || {};
                setLimitInfo({
                    limit: d.ProductLimit,
                    count: d.ProductCount,
                    left: d.ProductLimitLeft,
                });
            })
            .catch(() => {});
    }, [isEditMode]);
    const productLoadedRef = useRef(false); // Track if product data was loaded
    const savedPCodeRef = useRef(''); // the product code as saved (edit mode)
    const [refresh, setRefresh] = useState(0);
    const [showBottomBar, setShowBottomBar] = useState(false);
    const [editorState, setEditorState] = useState(() =>
        EditorState.createEmpty()
    );
    const navigate = useNavigate();
    const [isLoadingProduct, setIsLoadingProduct] = useState(false);
    const [productName, setProductName] = useState('');
    const [offerMessage, setOfferMessage] = useState('');
    const [video, setVideo] = useState('');
    const [HtmlDesc, setHtmlDesc] = useState('');
    const [buyPrice, setBuyPrice] = useState('');
    const [sellingPrice, setSellingPrice] = useState('');
    const [compare, setCompare] = useState('');
    const [pCode, setPCode] = useState('');
    const [colorName, setColorName] = useState('');
    const [quantity, setQuantity] = useState('');
    const [maxOrderQty, setMaxOrderQty] = useState('5');

    const [category, setCategory] = useState([]);
    const [selectCategory, setSelectCategory] = useState([]);
    const [selectva, setSelectva] = useState('no');

    const [categorydata, setCategoryData] = useState('');
    const [SubCategoryData, setSubCategoryData] = useState();
    const [SubCategoryId, setSubCategoryId] = useState('');
    // Multi-category support
    const [selectedCategories, setSelectedCategories] = useState([]); // array of category _id strings
    const [brandNames, setBrandNames] = useState([]); // array of brand name strings

    const [loading, setLoading] = useState(false);
    const [variationColorN, setVariationColorN] = useState('');
    const [checkvariation, setCheckvariation] = useState('no');
    const [productPage, setProductPage] = useState(1);
    const uid = localStorage.getItem('id');
    const [rows, setRows] = useState([]);
    const [rowsSize, setRowsSize] = useState([]);
    const [Trending, setTrendingNow] = useState('0');
    const [HotDeal, setHotDeal] = useState('0');
    const [showOrderNow, setShowOrderNow] = useState('Yes');
    const [RelatedProduct, setRelatedProduct] = useState('Same');
    const [images, setImages] = useState([]);
    const [errorFields, setErrorFields] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [aiModalOpen, setAiModalOpen] = useState(false);

    // SEO Fields
    const [metaTitle, setMetaTitle] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [slug, setSlug] = useState('');
    const [focusKeywords, setFocusKeywords] = useState([]);
    const [keywordInput, setKeywordInput] = useState('');
    const [brandName, setBrandName] = useState('');
    const [availability, setAvailability] = useState('in_stock');
    const [productCondition, setProductCondition] = useState('new');

    // FAQ Fields
    const [faqs, setFaqs] = useState([]);

    // Review Fields
    const [reviews, setReviews] = useState([]);
    const [averageRating, setAverageRating] = useState(0);
    const [totalReviews, setTotalReviews] = useState(0);

    // Breadcrumb Fields
    const [breadcrumbs, setBreadcrumbs] = useState([]);

    // SEO Helper Functions
    const generateSlug = useCallback((text) => {
        return text
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    }, []);

    // Auto-generate Meta Title from product name
    const generateMetaTitle = useCallback((name) => {
        if (!name) return '';
        // Limit to 60 characters for optimal SEO
        return name.length > 60 ? name.substring(0, 57) + '...' : name;
    }, []);

    // Auto-generate Meta Description from product description
    const generateMetaDescription = useCallback((description) => {
        if (!description) return '';
        // Strip HTML tags and limit to 160 characters
        const plainText = description.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
        return plainText.length > 160 ? plainText.substring(0, 157) + '...' : plainText;
    }, []);

    // Auto-generate breadcrumbs based on category selection
    const generateBreadcrumbs = useCallback(() => {
        const crumbs = [];
        let position = 1;

        // Home breadcrumb
        crumbs.push({ name: 'Home', url: '/', position: position++ });

        if (categorydata && category.length > 0) {
            const selectedCategory = category.find(cat => cat._id === categorydata);
            if (selectedCategory) {
                const categorySlug = selectedCategory.slug || generateSlug(selectedCategory.name);
                crumbs.push({
                    name: selectedCategory.name,
                    url: `/category/${categorySlug}`,
                    position: position++
                });

                if (SubCategoryId && SubCategoryData && SubCategoryData.length > 0) {
                    const selectedSubCategory = SubCategoryData.find(sub => sub._id === SubCategoryId);
                    if (selectedSubCategory) {
                        const subCategorySlug = selectedSubCategory.slug || generateSlug(selectedSubCategory.name);
                        crumbs.push({
                            name: selectedSubCategory.name,
                            url: `/category/${categorySlug}/${subCategorySlug}`,
                            position: position++
                        });
                    }
                }
            }
        }

        if (productName) {
            crumbs.push({
                name: productName,
                url: `/product/${slug || generateSlug(productName)}`,
                position: position++
            });
        }

        return crumbs;
    }, [categorydata, category, SubCategoryId, SubCategoryData, productName, slug, generateSlug]);

    const handleProductNameChange = useCallback((e) => {
        const name = e.target.value;
        setProductName(name);

        // Auto-generate slug if slug is empty or matches previous product name
        if (!slug || slug === generateSlug(productName)) {
            setSlug(generateSlug(name));
        }

        // Auto-generate meta title if empty or matches previous title
        if (!metaTitle || metaTitle === generateMetaTitle(productName)) {
            setMetaTitle(generateMetaTitle(name));
        }

        if (errorFields.productName) {
            setErrorFields((prev) => {
                const newErrors = { ...prev };
                delete newErrors.productName;
                return newErrors;
            });
        }
    }, [slug, productName, metaTitle, errorFields.productName, generateSlug, generateMetaTitle]);

    const handleEditorChange = useCallback((editorState) => {
        setEditorState(editorState);
        const contentState = editorState.getCurrentContent();
        const htmlContent = stateToHTML(contentState);
        setHtmlDesc(htmlContent);

        // Auto-generate meta description if empty or matches previous description
        if (!metaDescription || metaDescription === generateMetaDescription(HtmlDesc)) {
            setMetaDescription(generateMetaDescription(htmlContent));
        }

        if (errorFields.HtmlDesc) {
            setErrorFields((prev) => {
                const newErrors = { ...prev };
                delete newErrors.HtmlDesc;
                return newErrors;
            });
        }
    }, [metaDescription, HtmlDesc, errorFields.HtmlDesc, generateMetaDescription]);

    const addKeyword = () => {
        if (
            keywordInput.trim() &&
            !focusKeywords.includes(keywordInput.trim())
        ) {
            setFocusKeywords([...focusKeywords, keywordInput.trim()]);
            setKeywordInput('');
        }
    };

    const removeKeyword = (keyword) => {
        setFocusKeywords(focusKeywords.filter((k) => k !== keyword));
    };

    // A code longer than the limit is refused unless it is the code the
    // product was saved with before the limit (left unchanged).
    const pCodeTooLong = () =>
        productCodeTooLong(pCode) &&
        String(pCode).trim() !== String(savedPCodeRef.current).trim();

    const validateFields = () => {
        let errors = {};
        if (!productName) errors.productName = true;
        // Selling price is required (buy price and compare price are optional)
        if (!sellingPrice || Number(sellingPrice) <= 0) errors.sellingPrice = true;
        // Buy price is now optional - removed validation
        if (!HtmlDesc) errors.HtmlDesc = true;
        if (!categorydata && selectedCategories.length === 0) errors.categorydata = true;
        if (!images[0]?.url) errors.image = true;
        if (!pCode) errors.pCode = true;
        else if (pCodeTooLong()) errors.pCode = 'long';
        if (checkvariation === 'no') {
            if (!colorName) errors.colorName = true;
            if (quantity === '' || quantity === null || quantity === undefined) errors.quantity = true;
        }
        if (selectva === 'color' && !rows.length) {
            errors.variation = true;
        } else if (selectva === 'colorSize' && !rowsSize.length) {
            errors.variation = true;
        }

        // Validate variation pricing - selling price is required
        if (selectva === 'color' && rows.length > 0) {
            const hasValidPricing = rows.some(
                (row) =>
                    row.price &&
                    Number(row.price) > 0
            );
            if (!hasValidPricing) errors.variationPricing = true;
        } else if (selectva === 'colorSize' && rowsSize.length > 0) {
            const hasValidPricing = rowsSize.some(
                (colorVariant) =>
                    colorVariant.sizes &&
                    colorVariant.sizes.some(
                        (size) =>
                            size.price &&
                            Number(size.price) > 0
                    )
            );
            if (!hasValidPricing) errors.variationPricing = true;
        }

        setErrorFields(errors);
        return Object.keys(errors).length === 0;
    };

    const removeImage = (id) => {
        setImages((prevImages) => prevImages.filter((img) => img.id !== id));
    };

    const getCategory = useCallback(() => {
        myAxios
            .get(`/category/${uid}`)
            .then((res) => {
                const categories = res.data?.data || res.data || [];
                setCategory(categories);
            })
            .catch((error) => {
                console.error('Error loading categories:', error);
                setCategory([]);
            });
    }, [uid]);

    useEffect(() => {
        getCategory();
    }, [refresh, getCategory]);

    useEffect(() => {
        if (category && Array.isArray(category) && category.length > 0) {
            setSelectCategory([
                { _id: '', name: 'ক্যাটাগরি সিলেক্ট করুন' },
                ...category,
            ]);
        }
    }, [category]);

    // Auto-generate breadcrumbs when category, subcategory, or product name changes
    useEffect(() => {
        if (category && Array.isArray(category) && category.length > 0) {
            const generatedBreadcrumbs = generateBreadcrumbs();
            setBreadcrumbs(generatedBreadcrumbs);
        }
    }, [category, generateBreadcrumbs]);

    // Fetch product data when in edit mode
    useEffect(() => {
        if (isEditMode && id && category && Array.isArray(category) && category.length > 0 && !productLoadedRef.current) {
            productLoadedRef.current = true; // Mark as loaded to prevent re-fetch
            setIsLoadingProduct(true);
            myAxios
                .get(`/get-product/${id}`)
                .then((res) => {
                    const product = res.data;

                    // Basic Information
                    setProductName(product.productName || '');
                    setOfferMessage(product.offerMessage || '');
                    setVideo(product.video || '');

                    // Description - Convert HTML to EditorState
                    if (product.productDesc) {
                        const blocksFromHTML = convertFromHTML(product.productDesc);
                        const contentState = ContentState.createFromBlockArray(
                            blocksFromHTML.contentBlocks,
                            blocksFromHTML.entityMap
                        );
                        setEditorState(EditorState.createWithContent(contentState));
                        setHtmlDesc(product.productDesc);
                    }

                    // Pricing & Inventory
                    setBuyPrice(product.priceBuy || '');
                    setSellingPrice(product.priceSell || '');
                    setCompare(product.compare || '');
                    setPCode(product.pCode || '');
                    savedPCodeRef.current = product.pCode || '';
                    setColorName(product.color || '');
                    setQuantity(product.quantity || '');
                    setMaxOrderQty(product.maxOrderQty != null ? String(product.maxOrderQty) : '5');

                    // Categories
                    setCategoryData(product.category || '');
                    setSubCategoryId(product.SubCategory || '');

                    // Load subcategories for the selected category
                    if (product.category) {
                        const selectedCategory = category.find(
                            (cat) => cat._id === product.category
                        );
                        if (selectedCategory && selectedCategory.subcategories) {
                            setSubCategoryData(selectedCategory.subcategories);
                        }
                    }

                    // Images
                    if (product.images && product.images.length > 0) {
                        setImages(product.images);
                    }

                    // Multi-category and brand names
                    if (product.categories && Array.isArray(product.categories)) {
                        setSelectedCategories(product.categories);
                    }
                    if (product.brandNames && Array.isArray(product.brandNames)) {
                        setBrandNames(product.brandNames);
                    }

                    // Product Options
                    setTrendingNow(product.Trending || '0');
                    setHotDeal(product.HotDeal || '0');
                    setShowOrderNow(product.showOrderNow || 'Yes');
                    setRelatedProduct(product.RelatedProduct || 'Same');

                    // SEO Fields - Auto-generate if empty
                    const productSlug = product.slug || generateSlug(product.productName || '');
                    const productMetaTitle = product.metaTitle || generateMetaTitle(product.productName || '');
                    const productMetaDescription = product.metaDescription || generateMetaDescription(product.productDesc || '');

                    setMetaTitle(productMetaTitle);
                    setMetaDescription(productMetaDescription);
                    setSlug(productSlug);
                    setFocusKeywords(product.focusKeywords || []);
                    setBrandName(product.brandName || '');
                    setAvailability(product.availability || 'in_stock');
                    setProductCondition(product.productCondition || 'new');

                    // FAQ, Reviews, Breadcrumbs
                    setFaqs(product.faqs || []);
                    setReviews(product.reviews || []);
                    setAverageRating(product.averageRating || 0);
                    setTotalReviews(product.totalReviews || 0);

                    // Auto-generate breadcrumbs if empty
                    if (product.breadcrumbs && product.breadcrumbs.length > 0) {
                        setBreadcrumbs(product.breadcrumbs);
                    } else {
                        // Will be auto-generated by the breadcrumb useEffect
                        setBreadcrumbs([]);
                    }

                    // Variations
                    if (product.variation && product.variation !== 'no') {
                        setSelectva(product.variation);
                        setCheckvariation(product.variation);

                        if (product.variation === 'color' && product.variationData) {
                            setRows(product.variationData);
                        } else if (product.variation === 'colorSize' && product.variationData) {
                            setRowsSize(product.variationData);
                        }
                    }

                    setIsLoadingProduct(false);
                    toast.success('Product loaded for editing');
                })
                .catch((error) => {
                    console.error('Error loading product:', error);
                    toast.error('Failed to load product data');
                    setIsLoadingProduct(false);
                    productLoadedRef.current = false; // Reset on error
                });
        }
    }, [isEditMode, id, category, generateSlug, generateMetaTitle, generateMetaDescription]);

    // Scroll detection for bottom action bar
    useEffect(() => {
        const handleScroll = () => {
            // Show bottom bar when user scrolls down more than 200px
            if (window.scrollY > 200) {
                setShowBottomBar(true);
            } else {
                setShowBottomBar(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Helper functions for variations (used by VariantModal)
    function addRow() {
        const newRow = {
            colorName: variationColorN,
            price: '',
            PriceCompare: '',
            buy_price: '',
            quantity: '',
            ShipFrom: 'Dhaka',
            DeliveryTime: '1-3',
            image: '',
            gid: Date.now() + Math.floor(Math.random() * 10000),
        };
        setRows([...rows, newRow]);
        setVariationColorN('');
    }

    function handleInputChange(event, index) {
        const { name, value } = event.target;
        const updatedRows = [...rows];
        updatedRows[index][name] = value;
        setRows(updatedRows);
    }

    async function handleImageChange(event, index) {
        try {
            const file = event.target.files[0];
            if (file) {
                const imageUrl = await ImageUpload(file);
                const updatedRows = [...rows];
                updatedRows[index].image = imageUrl;
                setRows(updatedRows);
                toast.success('Image uploaded to CDN');
            }
        } catch (error) {
            toast.error('Failed to upload image');
        }
    }

    const addRowSize = () => {
        const now = Date.now();
        const newRow = {
            colorName: variationColorN,
            colorImage: '',
            sizes: [
                {
                    sizeName: '',
                    price: '',
                    buy_price: '',
                    quantity: '',
                    PriceCompare: '',
                    ShipFrom: 'Dhaka',
                    DeliveryTime: '1-3',
                    gid: now + 1 + Math.floor(Math.random() * 10000),
                },
            ],
            gid: now + Math.floor(Math.random() * 10000),
        };
        setRowsSize([...rowsSize, newRow]);
        setVariationColorN('');
    };

    function handleInputChangeSize(event, colorIndex, sizeIndex) {
        const { name, value } = event.target;
        const updatedRows = [...rowsSize];
        updatedRows[colorIndex].sizes[sizeIndex][name] = value;
        setRowsSize(updatedRows);
    }

    const handleImageChangeSize = async (event, colorIndex) => {
        try {
            const file = event.target.files[0];
            if (file) {
                const imageUrl = await ImageUpload(file);
                const updatedRows = [...rowsSize];
                updatedRows[colorIndex].colorImage = imageUrl;
                setRowsSize(updatedRows);
                toast.success('Image uploaded to CDN');
            }
        } catch (error) {
            toast.error('Failed to upload image');
        }
    };

    function handleRemoveSize(colorIndex, sizeIndex) {
        const updatedRows = [...rowsSize];
        updatedRows[colorIndex].sizes.splice(sizeIndex, 1);
        setRowsSize(updatedRows);
    }

    const handleImageSetWrapper = async (e, imageId) => {
        try {
            const updatedImages = await handleImageSetWrapperHelper(
                images,
                e,
                imageId
            );
            setImages(updatedImages);
            toast.success('Image uploaded');
        } catch (error) {
            // Handle error silently
        }
    };

    const handleUpdateAltText = (imageIndex, altText) => {
        const updatedImages = [...images];
        updatedImages[imageIndex] = {
            ...updatedImages[imageIndex],
            altText: altText,
        };
        setImages(updatedImages);
        toast.success('Alt text updated');
    };

    const handleUpdateVariantAltText = (variantIndex, altText, variantType) => {
        if (variantType === 'color') {
            const updatedRows = [...rows];
            updatedRows[variantIndex] = {
                ...updatedRows[variantIndex],
                altText: altText,
            };
            setRows(updatedRows);
            toast.success('Variant alt text updated');
        } else if (variantType === 'colorSize') {
            const updatedRowsSize = [...rowsSize];
            updatedRowsSize[variantIndex] = {
                ...updatedRowsSize[variantIndex],
                altText: altText,
            };
            setRowsSize(updatedRowsSize);
            toast.success('Variant alt text updated');
        }
    };

    const handleAIGenerated = useCallback((data, stats, sec) => {
        if (sec === 'altText' && data.altTexts) {
            setImages(prev => {
                const updated = [...prev];
                data.altTexts.forEach(({ index, altText }) => {
                    if (updated[index] && altText) {
                        updated[index] = { ...updated[index], altText };
                    }
                });
                return updated;
            });
            toast.success('Alt text generated for images!');
            setAiModalOpen(false);
            return;
        }

        // Product description -> Draft-JS EditorState
        if (data.productDescription) {
            let descHtml = data.productDescription;
            // Safety: strip any JSON wrapper if present
            if (descHtml.trim().startsWith('{')) {
                try {
                    const parsed = JSON.parse(descHtml);
                    descHtml = parsed.productDescription || parsed.content || descHtml;
                } catch {}
            }
            const blocksFromHTML = convertFromHTML(descHtml);
            if (blocksFromHTML.contentBlocks) {
                const contentState = ContentState.createFromBlockArray(
                    blocksFromHTML.contentBlocks,
                    blocksFromHTML.entityMap
                );
                setEditorState(EditorState.createWithContent(contentState));
                setHtmlDesc(descHtml);
            }
        }

        // Set product name and auto-generate slug + metaTitle
        if (data.productName) {
            if (!productName) setProductName(data.productName);
            setSlug(generateSlug(data.productName));
        }
        if (data.shortDescription) setOfferMessage(data.shortDescription);
        if (data.offerMessage) setOfferMessage(data.offerMessage);
        if (data.metaTitle) setMetaTitle(data.metaTitle);
        if (data.metaDescription) setMetaDescription(data.metaDescription);
        if (Array.isArray(data.focusKeywords) && data.focusKeywords.length) setFocusKeywords(data.focusKeywords);
        if (Array.isArray(data.faqs) && data.faqs.length) {
            setFaqs(data.faqs.map((f, i) => ({
                question: f.question || '',
                answer: f.answer || '',
                gid: String(Date.now() + i)
            })));
        }
        if (Array.isArray(data.reviews) && data.reviews.length) {
            const now = new Date();
            setReviews(data.reviews.map((r, i) => ({
                customerName: r.customerName || 'Customer',
                rating: r.rating || 5,
                reviewText: r.reviewText || '',
                reviewDate: new Date(now.getTime() - (i * 86400000 * (Math.floor(Math.random() * 14) + 1))).toISOString(),
                verified: true,
                helpful: Math.floor(Math.random() * 10),
                approved: true,
                gid: String(Date.now() + i)
            })));
            const avg = data.reviews.reduce((sum, r) => sum + (r.rating || 5), 0) / data.reviews.length;
            setAverageRating(Math.round(avg * 10) / 10);
            setTotalReviews(data.reviews.length);
        }

        setAiModalOpen(false);
        const duration = stats?.totalDuration ? ` (${stats.totalDuration}s)` : '';
        toast.success(`AI content generated!${duration}`);
    }, [productName, generateSlug]);

    const HandleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!validateFields()) {
                const missing = [];
                if (!productName) missing.push('Product Name');
                if (!sellingPrice || Number(sellingPrice) <= 0) missing.push('Selling Price');
                if (!HtmlDesc) missing.push('Description');
                if (!categorydata && selectedCategories.length === 0) missing.push('Category');
                if (!images[0]?.url) missing.push('Image');
                if (!pCode) missing.push('SKU Code');
                if (checkvariation === 'no' && !colorName) missing.push('Color Name');
                if (checkvariation === 'no' && (quantity === '' || quantity === null || quantity === undefined)) missing.push('Quantity');
                const problems = missing.length ? [`Missing: ${missing.join(', ')}`] : [];
                if (pCode && pCodeTooLong()) problems.push(`SKU can be at most ${PRODUCT_CODE_MAX} characters`);
                toast.error(problems.join('. '));
                return;
            }
            setLoading(true);

            const baseData = {
                productName,
                productDesc: HtmlDesc,
                offerMessage,
                video,
                buyPrice,
                sellingPrice,
                imageUrl: images[0]?.url || '',
                images,
                showOrderNow,
                HotDeal,
                Trending,
                RelatedProduct,
                category: categorydata,
                SubCategoryId,
                categories: selectedCategories.length > 0 ? selectedCategories : (categorydata ? [categorydata] : []),
                uid,
                pCode,
                compare,
                metaTitle: metaTitle || productName,
                metaDescription,
                slug: slug || generateSlug(productName),
                focusKeywords,
                brandName: brandName || (brandNames.length > 0 ? brandNames[0] : '') || localStorage.getItem('storeName') || '',
                brandNames: brandNames.length > 0 ? brandNames : (brandName ? [brandName] : []),
                storeName: localStorage.getItem('storeName') || '',
                availability,
                maxOrderQty: Number(maxOrderQty) >= 1 ? Math.floor(Number(maxOrderQty)) : 5,
                productCondition,
                faqs,
                reviews,
                averageRating,
                totalReviews,
                breadcrumbs,
            };

            const extraData =
                selectva === 'no'
                    ? {
                          colorName,
                          quantity,
                          gid: Date.now() + Math.floor(Math.random() * 10000),
                          variationData: [],
                      }
                    : selectva === 'color'
                    ? { variationData: rows, productPage, colorName: '', quantity: 0 }
                    : selectva === 'colorSize'
                    ? { variationData: rowsSize, colorName: '', quantity: 0 }
                    : {};

            const cleanFormData = {
                ...baseData,
                buyPrice:
                    buyPrice && !isNaN(Number(buyPrice)) ? Number(buyPrice) : 0,
                sellingPrice:
                    sellingPrice && !isNaN(Number(sellingPrice))
                        ? Number(sellingPrice)
                        : 0,
                compare: compare && !isNaN(Number(compare)) ? Number(compare) : 0,
                variation: selectva,
                ...extraData,
            };

            // Clean variation data if present
            if (
                cleanFormData.variationData &&
                Array.isArray(cleanFormData.variationData)
            ) {
                cleanFormData.variationData = cleanFormData.variationData.map(
                    (variant) => {
                        if (variant.sizes && Array.isArray(variant.sizes)) {
                            return {
                                ...variant,
                                sizes: variant.sizes.map((size) => ({
                                    ...size,
                                    price:
                                        size.price && !isNaN(Number(size.price))
                                            ? Number(size.price)
                                            : 0,
                                    buy_price:
                                        size.buy_price &&
                                        !isNaN(Number(size.buy_price))
                                            ? Number(size.buy_price)
                                            : 0,
                                    quantity:
                                        size.quantity &&
                                        !isNaN(Number(size.quantity))
                                            ? Number(size.quantity)
                                            : 0,
                                    PriceCompare:
                                        size.PriceCompare &&
                                        !isNaN(Number(size.PriceCompare))
                                            ? Number(size.PriceCompare)
                                            : 0,
                                    ShipFrom: size.ShipFrom || '',
                                    DeliveryTime: size.DeliveryTime || '',
                                })),
                            };
                        } else {
                            return {
                                ...variant,
                                price:
                                    variant.price && !isNaN(Number(variant.price))
                                        ? Number(variant.price)
                                        : 0,
                                buy_price:
                                    variant.buy_price &&
                                    !isNaN(Number(variant.buy_price))
                                        ? Number(variant.buy_price)
                                        : 0,
                                quantity:
                                    variant.quantity &&
                                    !isNaN(Number(variant.quantity))
                                        ? Number(variant.quantity)
                                        : 0,
                                PriceCompare:
                                    variant.PriceCompare &&
                                    !isNaN(Number(variant.PriceCompare))
                                        ? Number(variant.PriceCompare)
                                        : 0,
                            };
                        }
                    }
                );
            }

            const res = isEditMode
                ? await myAxios.post(`update-product/${id}`, cleanFormData)
                : await myAxios.post('add-product', cleanFormData);
            // The backend's own checks (category, product code, …) answer
            // with success: false; nothing was saved then.
            if (res.data && res.data.success === false) {
                toast.error(res.data.error || res.data.message || (isEditMode ? 'Failed to update product' : 'Failed to create product'));
                return;
            }
            toast.success(isEditMode ? 'Product updated successfully' : (res.data.message || 'Product created successfully'));

            navigate('/product', { replace: true });
        } catch (error) {
            console.error('Product submit error:', error.message);
            toast.error(isEditMode ? 'Failed to update product' : 'Failed to create product');
        } finally {
            setLoading(false);
        }
    };

    function handleRemove(index) {
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
    }

    function handleRemoveColorSize(index) {
        const updatedRows = [...rowsSize];
        updatedRows.splice(index, 1);
        setRowsSize(updatedRows);
    }

    return (
        <div className={styles['AddProduct-container']}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={true}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ zIndex: 99999 }}
            />

            {!isEditMode &&
                limitInfo &&
                limitInfo.limit !== -1 &&
                limitInfo.limit != null &&
                limitInfo.count >= Math.ceil(limitInfo.limit * 0.8) && (
                    <div
                        style={{
                            margin: '0 0 16px',
                            padding: '12px 16px',
                            borderRadius: 10,
                            fontSize: 14,
                            lineHeight: 1.5,
                            border: '1px solid',
                            ...(limitInfo.count >= limitInfo.limit
                                ? { background: '#fdecec', borderColor: '#f5a6a6', color: '#b91c1c' }
                                : { background: '#fff7e6', borderColor: '#f3d08a', color: '#b45309' }),
                        }}
                    >
                        {limitInfo.count >= limitInfo.limit ? (
                            <>
                                ⛔ প্রোডাক্ট লিমিট শেষ ({limitInfo.limit}টি প্রোডাক্ট)। নতুন প্রোডাক্ট যোগ করতে{' '}
                                <a href="/billing" style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>
                                    প্যাকেজ আপগ্রেড
                                </a>{' '}
                                করুন।
                            </>
                        ) : (
                            <>
                                ⚠️ আপনি <b>{limitInfo.count}/{limitInfo.limit}</b> প্রোডাক্ট ব্যবহার করেছেন — আর{' '}
                                <b>{limitInfo.left}টি</b> যোগ করতে পারবেন। বেশি প্রোডাক্টের জন্য{' '}
                                <a href="/billing" style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>
                                    প্যাকেজ আপগ্রেড
                                </a>{' '}
                                করুন।
                            </>
                        )}
                    </div>
                )}

            {/* Page Header */}
            <AIProductModal
                open={aiModalOpen}
                onClose={() => setAiModalOpen(false)}
                onGenerated={handleAIGenerated}
                isEditMode={isEditMode}
                existingData={{ productName, images, metaTitle, metaDescription, focusKeywords, faqs, offerMessage }}
            />

            <div className={styles['AddProduct-header']}>
                <h1>{isEditMode ? 'Edit product' : 'Add product'}</h1>
                <div className={styles['AddProduct-header-actions']}>
                    <button
                        type="button"
                        className={styles['AddProduct-button-ai']}
                        onClick={() => setAiModalOpen(true)}
                    >
                        AI Writing
                    </button>
                    <button
                        className={styles['AddProduct-button-secondary']}
                        onClick={() => navigate('/product')}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={HandleSubmit}
                        className={styles['AddProduct-submit-button']}
                        disabled={loading || isLoadingProduct}
                    >
                        {loading ? 'Saving...' : isEditMode ? 'Update product' : 'Save product'}
                    </button>
                </div>
            </div>

            {/* Loading State */}
            {isLoadingProduct && (
                <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    background: '#fff',
                    borderRadius: '12px',
                    margin: '20px 0'
                }}>
                    <p style={{ fontSize: '16px', color: '#6d7175' }}>Loading product data...</p>
                </div>
            )}

            {/* Main Layout */}
            <div className={styles['AddProduct-layout']}>
                {/* Main Content */}
                <div className={styles['AddProduct-main']}>
                    {/* Basic Information Card */}
                    <BasicInformation
                        productName={productName}
                        handleProductNameChange={handleProductNameChange}
                        editorState={editorState}
                        handleEditorChange={handleEditorChange}
                        uploadCallback={uploadCallback}
                        offerMessage={offerMessage}
                        setOfferMessage={setOfferMessage}
                        video={video}
                        setVideo={setVideo}
                        errorFields={errorFields}
                        setErrorFields={setErrorFields}
                        handleFieldChange={handleFieldChange}
                    />

                    {/* Pricing Card */}
                    <PricingInventory
                        buyPrice={buyPrice}
                        setBuyPrice={setBuyPrice}
                        sellingPrice={sellingPrice}
                        setSellingPrice={setSellingPrice}
                        compare={compare}
                        setCompare={setCompare}
                        pCode={pCode}
                        setPCode={setPCode}
                        colorName={colorName}
                        setColorName={setColorName}
                        quantity={quantity}
                        setQuantity={setQuantity}
                        maxOrderQty={maxOrderQty}
                        setMaxOrderQty={setMaxOrderQty}
                        checkvariation={checkvariation}
                        errorFields={errorFields}
                        setErrorFields={setErrorFields}
                        handleFieldChange={handleFieldChange}
                    />

                    {/* Variations Card */}
                    <VariantsSection
                        checkvariation={checkvariation}
                        setSelectva={setSelectva}
                        setCheckvariation={setCheckvariation}
                        setIsModalOpen={setIsModalOpen}
                        rows={rows}
                        rowsSize={rowsSize}
                        productPage={productPage}
                        setProductPage={setProductPage}
                        errorFields={errorFields}
                        setErrorFields={setErrorFields}
                        handleFieldChange={handleFieldChange}
                    />

                    {/* SEO Preview Card */}
                    <SEOPreview
                        productName={productName}
                        metaTitle={metaTitle}
                        metaDescription={metaDescription}
                        slug={slug}
                        focusKeywords={focusKeywords}
                        brandName={brandName}
                        availability={availability}
                        productCondition={productCondition}
                    />

                    {/* Product FAQ Card */}
                    <ProductFAQ
                        faqs={faqs}
                        setFaqs={setFaqs}
                        errorFields={errorFields}
                        setErrorFields={setErrorFields}
                        handleFieldChange={handleFieldChange}
                    />
                </div>

                {/* Sidebar */}
                <div className={styles['AddProduct-sidebar']}>
                    {/* Organization Card */}
                    <CategorySection
                        selectCategory={selectCategory}
                        categorydata={categorydata}
                        setCategoryData={setCategoryData}
                        category={category}
                        setSubCategoryData={setSubCategoryData}
                        SubCategoryData={SubCategoryData}
                        SubCategoryId={SubCategoryId}
                        setSubCategoryId={setSubCategoryId}
                        errorFields={errorFields}
                        setErrorFields={setErrorFields}
                        handleFieldChange={handleFieldChange}
                        selectedCategories={selectedCategories}
                        setSelectedCategories={setSelectedCategories}
                        brandNames={brandNames}
                        setBrandNames={setBrandNames}
                    />

                    {/* Product Options Card */}
                    <ProductOptions
                        showOrderNow={showOrderNow}
                        setShowOrderNow={setShowOrderNow}
                        RelatedProduct={RelatedProduct}
                        setRelatedProduct={setRelatedProduct}
                        Trending={Trending}
                        setTrendingNow={setTrendingNow}
                        HotDeal={HotDeal}
                        setHotDeal={setHotDeal}
                        errorFields={errorFields}
                        setErrorFields={setErrorFields}
                        handleFieldChange={handleFieldChange}
                    />

                    {/* Media Card */}
                    <div className={styles['AddProduct-card']}>
                        <h2 className={styles['AddProduct-card-title']}>
                            Media
                        </h2>
                        <MainImageSection
                            images={images}
                            onImageSet={handleImageSetWrapper}
                            onRemoveImage={removeImage}
                            onUpdateAltText={handleUpdateAltText}
                            error={errorFields.image}
                            productName={productName}
                        />
                    </div>

                    {/* Product Reviews Card */}
                    <ProductReviews
                        reviews={reviews}
                        setReviews={setReviews}
                        averageRating={averageRating}
                        setAverageRating={setAverageRating}
                        totalReviews={totalReviews}
                        setTotalReviews={setTotalReviews}
                        errorFields={errorFields}
                        setErrorFields={setErrorFields}
                        handleFieldChange={handleFieldChange}
                    />

                    {/* SEO Settings Card */}
                    <SEOSettings
                        metaTitle={metaTitle}
                        setMetaTitle={setMetaTitle}
                        metaDescription={metaDescription}
                        setMetaDescription={setMetaDescription}
                        slug={slug}
                        setSlug={setSlug}
                        generateSlug={generateSlug}
                        focusKeywords={focusKeywords}
                        keywordInput={keywordInput}
                        setKeywordInput={setKeywordInput}
                        addKeyword={addKeyword}
                        removeKeyword={removeKeyword}
                        brandName={brandName}
                        setBrandName={setBrandName}
                        availability={availability}
                        setAvailability={setAvailability}
                        productCondition={productCondition}
                        setProductCondition={setProductCondition}
                        errorFields={errorFields}
                        setErrorFields={setErrorFields}
                        handleFieldChange={handleFieldChange}
                    />

                    {/* Breadcrumbs auto-generated from category */}
                </div>

                {/* SEO Audit Card - Full Width */}
                <SEOAudit
                    productName={productName}
                    metaTitle={metaTitle}
                    metaDescription={metaDescription}
                    slug={slug}
                    focusKeywords={focusKeywords}
                    brandName={brandName}
                    availability={availability}
                    productCondition={productCondition}
                    images={images}
                    averageRating={averageRating}
                    totalReviews={totalReviews}
                    faqs={faqs}
                    breadcrumbs={breadcrumbs}
                    HtmlDesc={HtmlDesc}
                    sellingPrice={sellingPrice}
                    category={categorydata}
                    errorFields={errorFields}
                />

                {/* Schema markup auto-generated from product data */}
            </div>

            {/* Variant Modal */}
            <VariantModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                variationType={checkvariation}
                variationColorN={variationColorN}
                setVariationColorN={setVariationColorN}
                rows={rows}
                setRows={setRows}
                rowsSize={rowsSize}
                addRow={addRow}
                addRowSize={addRowSize}
                handleInputChange={handleInputChange}
                handleImageChange={handleImageChange}
                handleRemove={handleRemove}
                handleInputChangeSize={handleInputChangeSize}
                handleImageChangeSize={handleImageChangeSize}
                handleRemoveSize={handleRemoveSize}
                handleRemoveColorSize={handleRemoveColorSize}
                setRowsSize={setRowsSize}
                errorFields={errorFields}
                setErrorFields={setErrorFields}
                onUpdateVariantAltText={handleUpdateVariantAltText}
            />

            {/* Bottom Action Bar - appears on scroll */}
            {showBottomBar && (
                <div className={styles['AddProduct-bottom-bar']}>
                    <div className={styles['AddProduct-bottom-bar-content']}>
                        <button
                            className={styles['AddProduct-button-secondary']}
                            onClick={() => navigate('/product')}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={HandleSubmit}
                            className={styles['AddProduct-submit-button']}
                            disabled={loading || isLoadingProduct}
                        >
                            {loading ? 'Saving...' : isEditMode ? 'Update product' : 'Save product'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AddProduct;
